import {
  all,
  fork,
  call,
  takeEvery,
  put,
  select,
  delay,
} from 'redux-saga/effects';
import { push } from 'connected-react-router';
import type firebase from 'firebase/compat/app';
import { type Media, type User } from '@starly/starly-types';

import {
  type DocumentSnapshot, firestorage, firestore, functions, type QuerySnapshot, trackEvent, trackException,
} from 'global/firebase';
import { RouteTypes } from 'RouteTypes';

import { getFileExtension, isSupportedWebMediaFormat } from '../../util/supportedMedia';
import {
  disconnectUserSocialFailure,
  disconnectUserSocialRequest,
  disconnectUserSocialResponse,
  packBattleClose,
  sendFeedbackMessage,
  subscribe,
  updateAvatar,
  updateUsernameFailure,
  updateUsernameRequest,
  updateUsernameResponse,
  updateWalletRequest,
  updateWalletResponse,
  userRequest,
  userRequestByUsername,
  userResponse,
  userToggleLoading,
  userUpdateRequest,
  userUpdateResponse,
  writeAvatarImageRequest,
  writeAvatarImageResponse,
} from './userActions';

function waitForConversion(userId: string) {
  return new Promise((resolve) => {
    const unsubscribe = firestore.collection('users').doc(userId)
      .onSnapshot((snapshot) => {
        const snapData = snapshot.data();
        if (snapData && snapData.avatar.state === 'processed') {
          unsubscribe();
          resolve(snapData);
        }
      });
  });
}

function* watchUserRequest() {
  yield takeEvery(userRequest, function* takeEveryUserRequest(action) {
    try {
      const { payload: { id } } = action;
      const userRef: DocumentSnapshot = yield call(() => firestore.collection('users').doc(id).get());
      const user = (userRef.data() || {}) as User;

      if (!user.id) user.id = id;

      yield put(userResponse({ user }));

      if (user.avatar.state === 'unprocessed') {
        yield put(userToggleLoading(true));
        const { avatar }: User = yield call(waitForConversion, id);
        yield put(writeAvatarImageResponse({
          id,
          avatar,
        }));
        yield put(userToggleLoading(false));
      }
    } catch (error: any) {
      trackException(error.message);
    }
  });
}

function* watchUserUpdateRequest() {
  yield takeEvery(userUpdateRequest, function* takeEveryUserUpdateRequest(action) {
    try {
      const user = action.payload;

      if (!user.notifications) {
        user.notifications = { allow_emails: true };
      }

      yield call(() => firestore.collection('users').doc(user.id).set(user, { merge: true }));

      yield put(userUpdateResponse({ user }));
    } catch (error) {
      console.error(error);
    }
  });
}

function* watchWriteAvatarImageRequest() {
  yield takeEvery(writeAvatarImageRequest, function* takeEveryWriteAvatarImageRequest(action) {
    try {
      const {
        payload: {
          file,
          userId,
          avatarObj,
        },
      } = action;

      const extension = getFileExtension(file.name);
      const path = `users/${userId}/avatar${Date.now()}${extension}`;

      const uploadTaskSnapshot: firebase.storage.UploadTaskSnapshot = yield call(() => firestorage.ref().child(path).put(file));

      let avatar = avatarObj;
      if (avatarObj.type === 'video' && !isSupportedWebMediaFormat(file)) {
        yield delay(2000);
        const user: User = yield call(waitForConversion, userId);
        avatar = user.avatar;
      } else {
        const downloadUrl: string = yield call(() => uploadTaskSnapshot.ref.getDownloadURL());

        let newSize;
        if (avatarObj.type === 'video') {
          newSize = {
            mp4: downloadUrl,
          };
        } else if (avatarObj.type === 'image') {
          newSize = {
            url: downloadUrl,
          };
        }
        avatar.sizes = [newSize];
      }

      yield put(writeAvatarImageResponse({
        id: userId,
        avatar,
      }));
    } catch (error) {
      console.error(error);
    }
  });
}

function* watchUpdateAvatar() {
  yield takeEvery(updateAvatar, function* takeEveryUpdateAvatar(action) {
    try {
      const {
        payload: {
          avatarUrl,
          userId,
        },
      } = action;
      const avatar: Media = {
        type: 'image',
        sizes: [{
          url: avatarUrl,
        }],
        state: 'processed',
      };

      if (avatar) {
        yield firestore.collection('users').doc(userId).set({ avatar }, { merge: true });
        yield put(writeAvatarImageResponse({ id: userId, avatar }));
      }
    } catch (error) {
      console.error(error);
    }
  });
}

function* watchUserRequestByUsername() {
  yield takeEvery(userRequestByUsername, function* takeEveryUserRequestByUsername(action) {
    try {
      const { payload: { username } } = action;

      const snapshot: QuerySnapshot = yield call(() => firestore.collection('users')
        .where('username', '==', username.toLowerCase())
        .limit(1)
        .get());

      const user = snapshot.docs[0].data() as User;

      yield put(userResponse({ user }));
    } catch (error) {
      console.error(error);
    }
  });
}

function* watchUpdateWallet() {
  yield takeEvery(updateWalletRequest, function* takeEveryUpdateWalletRequest(action) {
    const { payload: { id, address } } = action;

    try {
      yield put(updateWalletResponse({
        id, address,
      }));
    } catch (e) {
      console.error(e.message);
    }
  });
}

function* watchUpdateUsername() {
  yield takeEvery(updateUsernameRequest, function* takeEveryUpdateUsernameRequest(action) {
    const { payload: { id, username } } = action;

    try {
      const updateUsername = functions.httpsCallable('updateUsername');

      yield call(() => updateUsername({ username }));
      yield put(updateUsernameResponse({ id, username }));
    } catch (error) {
      console.error(error);
      yield put(updateUsernameFailure({ id }));
    }
  });
}

function* watchDisconnectSocial() {
  yield takeEvery(
    disconnectUserSocialRequest,
    function* disconnectSocialWorker({ payload: { social } }) {
      const userId: string = yield select(({ auth }) => auth.userId);
      const socialName = social.replace(social[0], social[0].toUpperCase());

      try {
        const deleteSocial = functions.httpsCallable(`delete${socialName}`);

        yield call(() => deleteSocial());
        yield put(disconnectUserSocialResponse({ id: userId, social }));
      } catch (e) {
        console.error(e);
        yield put(disconnectUserSocialFailure({ id: userId, social }));
      }
    },
  );
}

function* watchSendMessage() {
  yield takeEvery(sendFeedbackMessage, function* sendMessageWorker({ payload: { messageData } }) {
    try {
      yield call(() => firestore.collection('contactUs').add(messageData));
    } catch (e) {
      console.error(e);
    }
  });
}

function* watchSubscribe() {
  yield takeEvery(subscribe, function* subscribeWorker({ payload: { email } }) {
    try {
      const addSubscriber = functions.httpsCallable('addSubscriber');

      yield call(() => addSubscriber({ email }));
      trackEvent('subscribe');
    } catch (e) {
      console.error(e);
    }
  });
}

function* watchPackBattleClose() {
  yield takeEvery(packBattleClose, function* packBattleCloseWorker({ payload: { id, redirect } }) {
    try {
      const clearPackBattleWinner = functions.httpsCallable('clearPackBattleWinner');

      yield call(() => clearPackBattleWinner({ id }));

      trackEvent('close_pack_battle');

      yield put(userRequest({ id }));

      if (redirect) {
        yield put(push(RouteTypes.VestingPrize));
      }
    } catch (e) {
      console.error(e);
    }
  });
}

export default function* userSaga() {
  yield all([
    fork(watchUserRequestByUsername),
    fork(watchUserRequest),
    fork(watchUserUpdateRequest),
    fork(watchWriteAvatarImageRequest),
    fork(watchUpdateAvatar),
    fork(watchUpdateUsername),
    fork(watchUpdateWallet),
    fork(watchDisconnectSocial),
    fork(watchSendMessage),
    fork(watchSubscribe),
    fork(watchPackBattleClose),
  ]);
}
